import React from 'react';
import { Assistant } from '@/types';
import { renderAssistantIcon } from '@/utils/RenderAssistantIcon';
import { extractColorsFromGradient } from '@/utils/colorUtils';

interface AssistantCardProps {
    assistant: Assistant;
    onSelect: (assistantId: string) => void;
}

export const AssistantCard: React.FC<AssistantCardProps> = ({ assistant, onSelect }) => {
    const gradientColors = extractColorsFromGradient(assistant.bgGradient || '');
    const iconColor = gradientColors[0] || assistant.accentColor || '#ffffff';
    const hoverColor = gradientColors[0] || assistant.accentColor || '#22d3ee';

    return (
        <button
            onClick={() => onSelect(assistant.id)}
            className="group relative p-4 sm:p-6 rounded-2xl bg-[#1a1a1a] border border-gray-700 transition-all duration-300 hover:-translate-y-1 text-left w-full h-full"
            style={{
                '--hover-color': hoverColor,
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%'
            } as React.CSSProperties}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${hoverColor}80`;
                e.currentTarget.style.boxShadow = `0 20px 25px -5px ${hoverColor}1A, 0 10px 10px -5px ${hoverColor}1A`;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#374151';
                e.currentTarget.style.boxShadow = '';
            }}
        >
            {/* Content section - grows to fill space */}
            <div className="flex flex-col items-center text-center" style={{ flex: '1 1 auto' }}>
                <div className={`p-2 sm:p-3 rounded-full bg-gradient-to-br ${assistant.bgGradient} mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center`}>
                    {renderAssistantIcon({
                        assistant: assistant,
                        iconColor: iconColor,
                        useGradientColor: true,
                        size: 'lg'
                    })}
                </div>
                <h3
                    className="text-base sm:text-lg font-bold text-white mb-1.5 sm:mb-2 transition-colors"
                    style={{
                        '--hover-text-color': hoverColor,
                    } as React.CSSProperties}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = hoverColor;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#ffffff';
                    }}
                >
                    {assistant.name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-300 line-clamp-3 mb-3 sm:mb-4">
                    {assistant.description}
                </p>
            </div>

            {/* Button section - stays at bottom and centered */}
            <div
                className="pt-2"
                style={{
                    flex: '0 0 auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                }}
            >
                <span
                    className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold text-black"
                    style={{ backgroundColor: hoverColor }}
                >
                    Start
                </span>
            </div>
        </button>
    );
};